import { useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

import { closeImageOverlay, closeOverlay } from 'redux/actions/ui'

/**
 * Overlays (comments, forms, todos, search and the fullscreen image carousel)
 * are stored in Redux state rather than in the URL. Because of that a browser
 * back/forward navigation changes the route without dismissing them, leaving
 * the overlay stuck on top of the page the user navigated to.
 *
 * This watcher must be rendered ABOVE the route <Switch> so it stays mounted
 * across navigations (the per-route AppLayout — and its overlay renderer —
 * unmounts and remounts on every route change, which is why detecting the
 * change there doesn't work). It closes any open overlay whenever the location
 * changes.
 *
 * It renders nothing.
 */
function RouteOverlayCloser() {
  const dispatch = useDispatch()
  const { pathname, search } = useLocation()
  const hasContentOverlay = useSelector((state) => Boolean(state.ui.overlay))
  const hasImageOverlay = useSelector((state) =>
    Boolean(state.ui.imageOverlayOpen),
  )

  // Read the latest open-state through a ref so the effect can depend solely on
  // the location. Depending on the overlay state itself would close an overlay
  // the instant it opens, since opening one doesn't change the route.
  const openStateRef = useRef({ hasContentOverlay, hasImageOverlay })
  openStateRef.current = { hasContentOverlay, hasImageOverlay }

  useEffect(() => {
    if (openStateRef.current.hasContentOverlay) {
      dispatch(closeOverlay())
    }
    if (openStateRef.current.hasImageOverlay) {
      dispatch(closeImageOverlay())
    }
  }, [pathname, search, dispatch])

  return null
}

export default RouteOverlayCloser
